import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/lib/auth-context";
import type { MdfRequestComment } from "./types";

// POST /api/admin/mdf/requests/:id/comments — internal admin posts a
// comment on a submitted MDF request. Optimistic append (Pattern A),
// mirrors apps/web/src/features/portal/mdf/use-add-mdf-comment.ts with
// the synthetic row's author_role flipped to "internal".
//
// The partner-side GET already exposes both author_roles, so an admin
// comment surfaces on the partner-side thread on the next refetch.

type Vars = { requestId: string; body: string };
type Context = {
  previous: MdfRequestComment[] | undefined;
  key: ReturnType<typeof queryKeys.mdf.requestComments>;
};

export function useAddInternalMdfComment() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation<{ id: string }, ApiClientError, Vars, Context>({
    mutationFn: ({ requestId, body }) =>
      apiFetch<{ id: string }>(
        `/admin/mdf/requests/${requestId}/comments`,
        {
          method: "POST",
          body: JSON.stringify({ body }),
        },
      ),
    onMutate: async ({ requestId, body }) => {
      const key = queryKeys.mdf.requestComments(requestId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<MdfRequestComment[]>(key);
      const optimistic: MdfRequestComment = {
        id: `temp-${crypto.randomUUID()}`,
        request_id: requestId,
        author_user_id: session?.userId ?? null,
        author_role: "internal",
        body,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<MdfRequestComment[]>(key, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to post comment");
    },
    onSettled: (_data, _err, { requestId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mdf.requestComments(requestId),
      });
    },
  });
}
